import React, { useState } from 'react';
import { ShoppingBag, ExternalLink } from 'lucide-react';
import { getCategoryInfo } from '../../utils/formatters';

export default function ProductCard({ product }) {
  const [imgError, setImgError] = useState(false);

  const targetUrl = product.affiliate_url || product.target_url || '#';
  const catInfo = getCategoryInfo(product.category_slug || product.category_name);

  const handleClick = (e) => {
    e.preventDefault();
    if (targetUrl && targetUrl !== '#') {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      onClick={handleClick}
      className="bg-gradient-to-b from-[#7A1018] via-[#650B12] to-[#48060B] border border-[#9E1B28]/80 hover:border-[#F5A623] rounded-2xl sm:rounded-3xl p-3 flex flex-col justify-between group transition-all duration-300 cursor-pointer shadow-lixi-card hover:shadow-2xl hover:shadow-red-950/80 hover:-translate-y-1.5 relative overflow-hidden select-none"
    >
      {/* Product Thumbnail */}
      <div className="relative aspect-square rounded-xl overflow-hidden bg-[#2D060A] mb-2 border border-white/10 shadow-inner">
        {imgError ? (
          <div className="w-full h-full flex flex-col items-center justify-center p-2 text-amber-200/80 bg-[#35070D] text-center">
            {product.platform === 'shopee' ? (
              <ShoppingBag size={24} className="text-[#EE4D2D] mb-1" />
            ) : (
              <span className="text-xl mb-1">🎵</span>
            )}
            <span className="text-[10px] text-amber-200/80">Hình ảnh sản phẩm</span>
          </div>
        ) : (
          <img
            src={product.image_url}
            alt={product.title}
            referrerPolicy="no-referrer"
            onError={() => setImgError(true)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        )}

        {/* Platform Badge (Attached to image corner) */}
        <div className="absolute top-2 right-2 z-10 pointer-events-none">
          {product.platform === 'shopee' ? (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EE4D2D]/95 text-white shadow-md flex items-center gap-1 backdrop-blur-xs">
              <ShoppingBag size={10} />
              <span>Shopee</span>
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/90 text-white shadow-md flex items-center gap-1 backdrop-blur-xs border border-white/20">
              <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.29 0 .58.04.85.12V9.35a6.34 6.34 0 0 0-.85-.06A6.33 6.33 0 0 0 3 15.62a6.34 6.34 0 0 0 9.17 5.63 6.34 6.34 0 0 0 3.6-5.63V8.82a8.28 8.28 0 0 0 3.82.93V6.69z"/>
              </svg>
              <span>TikTok</span>
            </span>
          )}
        </div>
      </div>

      {/* Content: Generous space for Description and Title */}
      <div className="flex-1 flex flex-col justify-between min-h-0">
        <div>
          {/* Category tag with Icon in Vietnamese */}
          <span className="text-[10px] font-bold text-[#F5A623] uppercase tracking-wider flex items-center gap-1 mb-1 truncate">
            <span>{catInfo.icon}</span>
            <span className="truncate">{catInfo.name}</span>
          </span>

          {/* Product Title and Description */}
          {product.description && product.description !== product.title ? (
            <>
              <h3
                className="text-xs sm:text-[13px] font-bold text-[#FFF5EB] line-clamp-2 leading-snug group-hover:text-amber-200 transition-colors"
                title={product.title}
              >
                {product.title}
              </h3>
              <p
                className="text-[11px] text-amber-100/70 line-clamp-2 leading-snug mt-1"
                title={product.description}
              >
                {product.description}
              </p>
            </>
          ) : (
            <h3
              className="text-xs sm:text-[13px] font-bold text-[#FFF5EB] line-clamp-3 leading-snug group-hover:text-amber-200 transition-colors"
              title={product.title}
            >
              {product.title}
            </h3>
          )}
        </div>

        {/* Nhận Voucher Button (Smaller, compact, elegant) */}
        <div className="mt-2 pt-2 border-t border-[#8E1722]/50">
          <button
            type="button"
            onClick={handleClick}
            className="w-full text-[#240306] bg-gradient-to-r from-[#F5A623] to-[#E58F10] group-hover:brightness-110 active:scale-95 py-1.5 px-2.5 rounded-lg text-[11px] sm:text-xs font-black flex items-center justify-center gap-1 transition-all shadow-sm group-hover:shadow-amber-500/20"
          >
            <span>Nhận Voucher</span>
            <ExternalLink size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}
